'use client';
/* Built In Imports */
import React, { useEffect, useState } from 'react';

/* External Imports */
import { Box, Divider, Text , Link} from '@chakra-ui/react';
import { AiOutlinePlus } from 'react-icons/ai';
import {
  HiOutlineMinus,
  HiOutlineMinusCircle,
  HiOutlinePlusCircle,
} from 'react-icons/hi2';

/* Internal Imports */
/* Components */
import LazyLoadImageComponent from '@app-ps/Components/Ui/LazyLoadImageComponent';
import config from '@config';
import { render } from 'datocms-structured-text-to-html-string';
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
//import { Link } from 'react-scroll';
/**
 * Renders the FAQ content component
 *
 * @param {object} sectionContent - Data for section
 * @param {string} region - Region for the section
 * @param {string} language - Language for the section
 * @returns {ReactElement} FAQ content component
 */

const FaqContents = ({ sectionContent, region, lang }) => {
  // State to manage which FAQ item is open
  const [openIndexes, setOpenIndexes] = useState({});
  const [answerVisibleIndexes, setAnswerVisibleIndexes] = useState({});

  useEffect(() => {
    const containers = document.getElementsByClassName('faqAnsPs');
    Array.from(containers).forEach(container => {
      const links = container.querySelectorAll('a[target="_blank"], a');
      links.forEach(anchor => {
        anchor.removeAttribute('target');
        anchor.onclick = e => e.stopPropagation();
      });
    });
  }, [openIndexes, answerVisibleIndexes]);

  const handleToggle = categoryIndex => {
    setOpenIndexes(prevState => ({
      ...prevState,
      [categoryIndex]: !prevState[categoryIndex],
    }));
  };

  const handleAnswerToggle = (categoryIndex, questionIndex) => {
    setAnswerVisibleIndexes(prevState => ({
      ...prevState,
      [categoryIndex]: {
        ...prevState[categoryIndex],
        [questionIndex]: !prevState[categoryIndex]?.[questionIndex],
      },
    }));
  };

  return (
    <Box
      width="100%"
      bg="#FEFAF6"
      mt="-2%"
      pt={{ base: '5rem', lg: '8rem' }}
      pb={{ base: '15rem', lg: '8rem' }}
      sx={{
        clipPath: {
          base: 'polygon(0 0, 100% 0, 100% 94%, 0% 100%)',
          md: 'polygon(0 0, 100% 0, 100% 88%, 0% 100%)', // Default clipPath for smaller screens
          lg: 'polygon(0 0, 100% 0, 100% 92%, 0% 100%)',
          // ClipPath for lg and larger
        },
      }}
    >
      {/* box for heading */}
      <Box
        display={'flex'}
        gap={{ md: '.5rem' }}
        fontSize={{ md: '2.25rem', base: '1.5rem' }}
        letterSpacing={'0.16px'}
        flexDirection={{ lg: 'row', base: 'column' }}
        textAlign={{ lg: 'center' }}
        justifyContent={{ lg: 'center', base: 'center' }}
        alignItems={'center'}
      >
        <Text
          lineHeight={{ lg: '2.8125rem', base: '1.8rem' }}
          fontWeight={400}
          fontFamily="var(--FedraSerifDisplay-Regular)"
          color="#020912"
        >
          {sectionContent[0]?.titleText}
        </Text>
        <Text
          color="#010509"
          lineHeight={{ md: '2.7rem', base: '1.8rem' }}
          fontWeight={700}
          fontFamily="var(--FedraSerifDisplay-Bold)"
        >
          {sectionContent[0]?.titleSubtext}
        </Text>
      </Box>

      {/* container for faq */}
      <Box
        maxWidth={{ lg: '973px', base: '100%' }}
        mx={'auto'}
        mt={{ md: '3.5rem', base: '1.5rem' }}
      >
        {/* map through the FAQ sectionContent */}
        {sectionContent[1]?.faqSections.map((category, categoryIndex) => (
          <Box key={categoryIndex}>
            <Box
              bg="#F1EDE9"
              px={{ md: '1.5rem', base: '1.625rem' }}
              py={{ md: '1.8rem', base: '1.5rem' }}
              mb={{ base: '1rem' }}
            >
              {/* Box for topic heading */}
              <Box
                display="flex"
                alignItems="center"
                gap={{ lg: '2.5rem', base: '1rem' }}
                color="#0E1216"
                fontSize={{ base: '1.25rem', md: '1.5rem' }}
                fontFamily="var(--FedraSansStd-medium)"
                fontWeight={500}
                lineHeight={{ base: '2rem', md: '2.4rem' }}
                letterSpacing="0.32px"
                cursor="pointer"
                onClick={() => handleToggle(categoryIndex)}
              >
                {openIndexes[categoryIndex] ? (
                  <HiOutlineMinusCircle
                    style={{ color: '#797979', width: '18px', height: '18px' }}
                  />
                ) : (
                  <HiOutlinePlusCircle
                    style={{ color: '#797979', width: '18px', height: '18px' }}
                  />
                )}
                {category.title}
              </Box>

              {/* Container box for questions and their answer */}
              {openIndexes[categoryIndex] && (
                <Box mt={{ lg: '1.5rem' }}>
                  {category.faqs.map((faq, questionIndex) => (
                    <React.Fragment key={questionIndex}>
                      <Box
                        py={{ base: '1.2rem', md: '1.8rem' }}
                        pl={{ lg: '3.75rem' }}
                        pr={{ lg: '0.5rem', base: '0.75rem' }}
                      >
                        <Box
                          onClick={() =>
                            handleAnswerToggle(categoryIndex, questionIndex)
                          }
                          cursor="pointer"
                        >
                          {/* Question */}
                          <Box
                            fontSize={{ base: '1rem', md: '1.25rem' }}
                            fontFamily="var(--FedraSansStd-medium)"
                            fontWeight={500}
                            lineHeight={{ base: '1.6rem', md: '2rem' }}
                            letterSpacing="0.32px"
                            display="flex"
                            justifyContent="space-between"
                          >
                            <Text color="#0E1216">{faq.question}</Text>
                            <Box
                              sx={{
                                fontSize: '1.2rem',
                                color: '#797979',
                                marginTop: '1%',
                                '@media (min-width: 48em)': {
                                  fontSize: '2rem',
                                  color: '#797979',
                                  marginTop: '0',
                                },
                              }}
                            >
                              {answerVisibleIndexes[categoryIndex]?.[
                                questionIndex
                              ] ? (
                                <HiOutlineMinus
                                  fontSize="1.5rem"
                                  color="#797979"
                                />
                              ) : (
                                <AiOutlinePlus fontSize="1.5rem" />
                              )}
                            </Box>
                          </Box>

                          {/* Answer */}
                          {answerVisibleIndexes[categoryIndex]?.[
                            questionIndex
                          ] && (
                            <Box
                              fontSize={{ base: '1rem', md: '1rem' }}
                              fontFamily="var(--FedraSansStd-book)"
                              fontWeight={400}
                              maxWidth={{ lg: '815px' }}
                              lineHeight={{ base: '1.4rem', md: '1.6rem' }}
                              letterSpacing="0.32px"
                            >
                              <StructuredTextParser
                                className={'faqAnsPs'}
                                mt={'8px'}
                                sectionContent={faq.answer.value}
                                str={render(faq.answer.value)}
                                color={'#0E1216'}
                                maxW={'100%'}
                                lineHeight={{base: '1.8rem', md: '2rem'}}
                                fontSize={{
                                  base: '.875rem',
                                  md: '16px',
                                }}
                                fontWeight={400}
                              />
                              <style>
                                {`.faqAnsPs a u {
                                  text-decoration: none;
                                }`}
                              </style>
                              {/* {faq.answer.value.document.children.map(
                                (child, childIndex) => (
                                  <p
                                    key={childIndex}
                                    style={{ marginTop: '0.5rem' }}
                                  >
                                    {child.children.map((span, spanIndex) => (
                                      <Box
                                        as="span"
                                        key={spanIndex}
                                        fontSize={{
                                          base: '.875rem',
                                          md: '1rem',
                                        }}
                                        color="#0E1216"
                                      >
                                        {span.value}
                                        {span?.url && span?.url.startsWith("mailto:") && (
                                        <Link
                                        href={span?.url}
                                        isExternal
                                        >
                                        {span?.children[0]?.value}
                                        </Link>
                                        )}
                                      </Box>
                                    ))}
                                  </p>
                                )
                              )} */}
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {questionIndex < category.faqs.length - 1 && (
                        <Divider
                          borderColor="#0E121633"
                          borderWidth="1px"
                          width="100%"
                        />
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>
      <Box
        display={{ base: 'flex' }}
        maxH="135px"
        maxW="470px"
        width={{ base: '156px', md: '200px', xl: '470px', '2xl': '470px' }}
        height={{ base: '45px', xl: '135px' }}
        justifyContent={'center'}
        mt={{ base: '3rem', lg: '6rem' }}
        mx="auto"
      >
        <LazyLoadImageComponent
          src={`${config.imgPath}/d/46272/1721308187-workshopkolam.png`}
          alt="Workshop Kolam"
          width="100%"
          height="100%"
        />
      </Box>
    </Box>
  );
};

export default FaqContents;
